/**
 * Council Trail v0 — Lens state context.
 *
 * Lens is the operator's view-mode over the Council substrate:
 *   - canopy : top-level surface (all peers visible at fan resolution)
 *   - single : focus on one peer's spine
 *   - multi  : focus on N peers (subset)
 *   - trail  : pinned to a specific trail
 *
 * Provider hydrates state from URL params first, then localStorage,
 * then default. On every change it writes through to BOTH (so back/forward
 * navigation works AND state survives reload without an outbound link).
 */
import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
} from 'react';

import type { LensMode } from '../types/council';
import { deriveMode } from '../utils/lensVisibility';
import {
  lensStateFromLocalStorage,
  lensStateFromUrlParams,
  lensStateToLocalStorage,
  lensStateToUrlParams,
} from '../utils/lensSerialize';

export interface LensState {
  mode: LensMode;
  activePersonaIds: Set<string>;
  activeTrailId: string | null;
  cursorTurnId: string | null;
  cursorMode: 'reply' | 'fork';   // §3 — fork-compose discriminator (NOT serialized; ephemeral)
  cursorReroll?: boolean;          // §3 — transient reroll intent (bubble → ConversationInterface useEffect); NOT serialized
  // SP3 H.2: sticky write-target for `multi` lens — the picker remembers
  // the last persona the operator addressed so subsequent sends default
  // to the same trail. Falls back to last-authored-terminal when the
  // remembered persona leaves the active set.
  lastMultiTarget?: string | null;
}

export interface LensContextValue extends LensState {
  setLens: (next: Partial<LensState>) => void;
  cycle: (direction: 1 | -1) => void;
  clear: () => void;
}

const DEFAULT_STATE: LensState = {
  mode: 'canopy',
  activePersonaIds: new Set(),
  activeTrailId: null,
  cursorTurnId: null,
  cursorMode: 'reply',
};

/** §3 — exported alias for tests + write-target consumers (= the initial lens). */
export const INITIAL_LENS = DEFAULT_STATE;

const CYCLE_ORDER: LensMode[] = ['canopy', 'single', 'multi', 'trail'];

/**
 * P3 SP1 — mode is a DERIVATION of (set, trail); trail is sticky but any set
 * interaction exits it. Normalization lives inside setLens itself (every
 * caller normalized, no un-normalized escape hatch) — the spec's
 * `setLensNormalized` IS `setLens` (plan delta 5). Keeps write-target,
 * serialization, and the pill from ever reading a stale or un-producible
 * (mode, set) combination.
 */
export function normalizeNext(prev: LensState, patch: Partial<LensState>): LensState {
  const next = { ...prev, ...patch };
  if ('activeTrailId' in patch && patch.activeTrailId) {
    next.activePersonaIds = new Set();           // entering trail clears persona focus
  } else if ('activePersonaIds' in patch) {
    next.activeTrailId = null;                   // set interaction exits trail
  }
  next.mode = deriveMode(next.activePersonaIds, next.activeTrailId);
  return next;
}

/** Hydration residue (URL/LS can encode states the controls can't produce). */
function normalizeState(s: LensState): LensState {
  const set = s.activeTrailId ? new Set<string>() : s.activePersonaIds;
  return { ...s, activePersonaIds: set, mode: deriveMode(set, s.activeTrailId) };
}

export const LensContext = createContext<LensContextValue | null>(null);

export function LensProvider({
  sessionId,
  children,
}: {
  sessionId: string;
  children: React.ReactNode;
}) {
  const [state, setState] = useState<LensState>(() => hydrateInitial(sessionId));

  useEffect(() => {
    lensStateToLocalStorage(sessionId, state);
    try {
      const params = lensStateToUrlParams(state);
      const url = new URL(window.location.href);
      for (const key of ['lens', 'peers', 'trail', 'cursor']) {
        url.searchParams.delete(key);
      }
      params.forEach((v, k) => url.searchParams.set(k, v));
      window.history.replaceState({}, '', url);
    } catch {
      // jsdom or environments without a real window.history; degrade silently.
    }
  }, [sessionId, state]);

  const setLens = useCallback(
    (next: Partial<LensState>) => setState(prev => normalizeNext(prev, next)),
    [],
  );

  const cycle = useCallback((direction: 1 | -1) => {
    setState(prev => {
      const idx = CYCLE_ORDER.indexOf(prev.mode);
      const nextIdx = (idx + direction + CYCLE_ORDER.length) % CYCLE_ORDER.length;
      return { ...prev, mode: CYCLE_ORDER[nextIdx] };
    });
  }, []);

  const clear = useCallback(() => setState(DEFAULT_STATE), []);

  return (
    <LensContext.Provider value={{ ...state, setLens, cycle, clear }}>
      {children}
    </LensContext.Provider>
  );
}

function hydrateInitial(sessionId: string): LensState {
  try {
    const fromUrl = lensStateFromUrlParams(
      new URLSearchParams(window.location.search),
    );
    if (fromUrl.mode) {
      return normalizeState({
        ...DEFAULT_STATE,
        ...fromUrl,
        activePersonaIds: fromUrl.activePersonaIds ?? new Set(),
      });
    }
  } catch {
    // window.location.search absent — fall through
  }

  const fromLs = lensStateFromLocalStorage(sessionId);
  if (fromLs?.mode) {
    return normalizeState({
      ...DEFAULT_STATE,
      ...fromLs,
      activePersonaIds: fromLs.activePersonaIds ?? new Set(),
    });
  }

  return DEFAULT_STATE;
}
